import { Box, Divider, Progress } from "@chakra-ui/react";
import { useRef, useState } from "react";
import hotToast from "react-hot-toast";
import { GrTest } from "react-icons/gr";
import { MdContentCopy } from "react-icons/md";
import { VscTools } from "react-icons/vsc";
import { scrollToBottom } from "../../utils/dom.utils";
import TypeEffect from "../TypeEffect";
import ToolsOutputFile from "./ToolsOutputFile";

type ToolOutputProps = {
    toolName?: string;
    isExecuting?: boolean;
    outputData?: {
        text:
            | string
            | {
                  complete_output: string;
                  system_arguments: any;
              };
        file: string;
    };
};

const ToolsOutput = ({ toolName, outputData, isExecuting }: ToolOutputProps) => {
    const [showCopyBtn, setShowCopyBtn] = useState(false);
    const responseContainerRef = useRef<HTMLDivElement>(null);

    const copyToClipboard = async () => {
        try {
            if (outputData?.text) {
                const textToCopy =
                    typeof outputData.text === "string"
                        ? outputData.text
                        : outputData.text.complete_output;
                if (textToCopy) {
                    // eslint-disable-next-line no-undef
                    await navigator.clipboard.writeText(textToCopy);
                    hotToast("Copied");
                }
            }
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    const processedOutputText = outputData?.text
        ? typeof outputData.text === "string"
            ? outputData.text.replace(/(\n\s+)/g, (match) => match.replace(/ +/g, ""))
            : outputData.text.complete_output.replace(/(\n\s+)/g, (match) =>
                  match.replace(/ +/g, "")
              )
        : "";

    return (
        <div className="container-inner rounded-lg w-full bg-[#2b2b2b] bg-old-[#373737] h-full">
            {!isExecuting && !outputData && (
                <div className="no-test h-full flex flex-col justify-center items-center">
                    <div className="flex items-center gap-[10px]">
                        <GrTest size={"24px"} />
                        <p className="text-[18px] font-medium">No Data to display</p>
                    </div>
                    <p className="text-[13px] text-center mt-[10px] text-slate-300">
                        The output data will display here. Run test to preview.
                    </p>
                </div>
            )}

            {isExecuting && (
                <div className="test-running h-full flex flex-col justify-center items-center">
                    <div className="flex items-center gap-[10px]">
                        <GrTest size={"24px"} />
                        <p className="text-[18px] font-medium">Executing Tool..</p>
                    </div>
                    <p className="text-[13px] text-center mt-[10px] text-slate-300">
                        The output will display here after execution.
                    </p>
                    <div className="mt-[10px] w-[100px]">
                        <Progress
                            size="xs"
                            isIndeterminate
                            colorScheme="green"
                            borderRadius={"100px"}
                        />
                    </div>
                </div>
            )}

            {!isExecuting && outputData && (
                <div className="w-full h-full grid grid-rows-[45px_auto]">
                    <div className="px-4 flex justify-between items-center h-[45px]">
                        <h4 className="text-[14px] font-bold text-slate-200">
                            <VscTools size={"18px"} className="inline-block text-green-600 " />
                            &nbsp;
                            {toolName}
                        </h4>
                    </div>
                    {/* <Divider /> */}
                    <div
                        ref={responseContainerRef}
                        className="output-box h-full px-4 overflow-y-auto"
                    >
                        {outputData?.file && (
                            <div className="file-output">
                                <Box position="relative" py="5">
                                    <Divider />
                                    <div className="absolute px-[4px] top-[50%] translate-y-[-50%] left-[4px] bg-[#2b2b2b]">
                                        <span className="font-semibold text-[13px]">File(s)</span>
                                    </div>
                                </Box>
                                <ToolsOutputFile fileName={outputData.file} />
                            </div>
                        )}

                        {outputData?.text && !outputData?.file && (
                            <div className="">
                                <Box position="relative" py="5">
                                    <Divider />
                                    <div className="absolute px-[4px] top-[50%] translate-y-[-50%] left-[4px] bg-[#2b2b2b]">
                                        <span className="font-semibold text-[13px]">Text</span>
                                    </div>
                                    <div className="absolute bg-[#2b2b2b] px-[4px] top-[50%] translate-y-[-50%] right-[4px]">
                                        {showCopyBtn && (
                                            <button
                                                onClick={copyToClipboard}
                                                className="bg-[#3f3f3f] flex items-center justify-center h-[30px] w-[30px] rounded-[6px]"
                                                title="Copy"
                                            >
                                                <MdContentCopy />
                                            </button>
                                        )}
                                    </div>
                                </Box>
                                <p className="text-[14px] leading-[150%] text-white whitespace-pre-wrap">
                                    <TypeEffect
                                        text={processedOutputText}
                                        speed={10}
                                        onComplete={() => setShowCopyBtn(true)}
                                        scrollToBottomHandler={() =>
                                            scrollToBottom(responseContainerRef)
                                        }
                                    />
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ToolsOutput;
